import { API_BASE_URL } from '../constants';
let pageId = freshId();
const failures: string[] = [];
const requestIds: string[] = [];
function freshId() { return Array.from(typeof crypto !== 'undefined' ? crypto.getRandomValues(new Uint8Array(16)) : Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256)), n => n.toString(16).padStart(2, '0')).join(''); }
export function supportHeaders() { return { 'X-Voyager-Page': pageId }; }
export function supportContext() { return { page_id: pageId, failures: [...failures], request_ids: [...requestIds], build: process.env.REACT_APP_BUILD_SHA || 'unknown' }; }
export function noteFailure(kind: string, requestId?: string) {
  failures.push(kind); if (failures.length > 20) failures.shift();
  if (requestId && /^[a-f0-9]{32}$/.test(requestId)) { requestIds.push(requestId); if (requestIds.length > 20) requestIds.shift(); }
}
// What is on screen right now: a trace session (an answer, a run) or a durable record with no trace of its
// own (`kind:id`, a workflow execution). Components RETAIN a reference while mounted and release it when they
// unmount (`useSupportRef` / `<SupportRef>`), so what a support link sends is what is shown, not everything
// ever shown. Counted, because the same run can be on screen twice. The server finds the background job
// behind each — for this account and workspace only.
const SESSION = /^[a-zA-Z0-9_-]{1,100}$/;
const OCCURRENCE = /^[a-z_]{1,40}:[A-Za-z0-9_.:-]{1,100}$/;
const mounted = new Map<string, { count: number; at: number }>();
let tick = 0;
export function retainSupportRef(kind: 'session' | 'occurrence', id?: string | null): () => void {
  if (!id || !(kind === 'session' ? SESSION : OCCURRENCE).test(id)) return () => {};
  const key = `${kind}|${id}`;
  const entry = mounted.get(key);
  mounted.set(key, { count: (entry?.count || 0) + 1, at: ++tick });
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const now = mounted.get(key);
    if (!now) return;
    if (now.count <= 1) mounted.delete(key); else mounted.set(key, { ...now, count: now.count - 1 });
  };
}
function onScreen(kind: 'session' | 'occurrence'): string[] {
  const prefix = `${kind}|`;
  return Array.from(mounted.entries()).filter(([k]) => k.startsWith(prefix))
    .sort((x, y) => x[1].at - y[1].at).map(([k]) => k.slice(prefix.length));
}
/** On-screen trace sessions, most recently mounted last. */
export function notedSessions() { return onScreen('session'); }
/** On-screen records, most recently mounted last. */
export function notedOccurrences() { return onScreen('occurrence'); }
// a workspace switch starts a new page; what is mounted stays registered until it unmounts
export function resetSupportContext() { pageId = freshId(); failures.length = 0; requestIds.length = 0; }
export function installErrorCapture() {
  window.addEventListener('error', () => noteFailure('javascript'));
  window.addEventListener('unhandledrejection', () => noteFailure('unhandled_rejection'));
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = new URL(input instanceof Request ? input.url : String(input), window.location.href);
    const apiOrigin = new URL(API_BASE_URL || window.location.origin, window.location.origin).origin;
    if (url.origin !== apiOrigin || !url.pathname.startsWith('/api/')) return originalFetch(input, init);
    const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
    Object.entries(supportHeaders()).forEach(([k, v]) => headers.set(k, v));
    const capturedPage = pageId;
    try {
      const response = await originalFetch(input, { ...init, headers });
      if (!response.ok && capturedPage === pageId) noteFailure('network', response.headers.get('x-request-id') || undefined);
      return response;
    } catch (error) {
      if (capturedPage === pageId && (error as any)?.name !== 'AbortError') noteFailure('network');
      throw error;
    }
  };
}
